<template>
  <div id="appvue">
    <router-view></router-view>
    <vue-snotify></vue-snotify>
    <transition name="app-update">
      <section
        v-if="updateAvailable"
        class="app-update-notice"
        role="status"
        aria-live="polite"
      >
        <div class="app-update-notice__icon" aria-hidden="true">
          <CIcon name="cil-reload" />
        </div>
        <div class="app-update-notice__content">
          <strong>Neue Version verfügbar</strong>
          <span>Bitte ungespeicherte Eingaben sichern und die Anwendung neu laden.</span>
        </div>
        <button type="button" class="app-update-notice__button" @click="reloadApplication">
          Jetzt neu laden
        </button>
      </section>
    </transition>
  </div>
</template>

<script>
const UPDATE_CHECK_INTERVAL = 60 * 1000

export default {
  name: 'app',
  data () {
    return {
      currentBuildVersion: process.env.VUE_APP_BUILD_VERSION || '',
      updateAvailable: false,
      updateCheckTimer: null
    }
  },
  mounted () {
    this.removeReloadMarker()
    window.addEventListener('etime:update-required', this.showUpdateNotice)
    window.addEventListener('focus', this.checkForUpdate)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    if (window.__ETIME_UPDATE_REQUIRED__) {
      this.showUpdateNotice()
    }

    if (this.currentBuildVersion) {
      this.checkForUpdate()
      this.updateCheckTimer = window.setInterval(this.checkForUpdate, UPDATE_CHECK_INTERVAL)
    }
  },
  beforeDestroy () {
    window.removeEventListener('etime:update-required', this.showUpdateNotice)
    window.removeEventListener('focus', this.checkForUpdate)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.clearInterval(this.updateCheckTimer)
  },
  methods: {
    async checkForUpdate () {
      if (!this.currentBuildVersion || this.updateAvailable) {
        return
      }

      try {
        const response = await window.fetch(`/version.json?_=${Date.now()}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        })
        const contentType = response.headers.get('content-type') || ''
        if (!response.ok || !contentType.includes('application/json')) {
          return
        }

        const release = await response.json()
        if (release.version && release.version !== this.currentBuildVersion) {
          this.showUpdateNotice()
        }
      } catch (error) {
        // Update checks must never interrupt normal form usage while offline.
      }
    },
    handleVisibilityChange () {
      if (document.visibilityState === 'visible') {
        this.checkForUpdate()
      }
    },
    showUpdateNotice () {
      this.updateAvailable = true
    },
    reloadApplication () {
      const url = new URL(window.location.href)
      url.searchParams.set('_etime_reload', Date.now().toString())
      window.location.assign(url.toString())
    },
    removeReloadMarker () {
      const url = new URL(window.location.href)
      if (!url.searchParams.has('_etime_reload')) {
        return
      }

      url.searchParams.delete('_etime_reload')
      window.history.replaceState(window.history.state, document.title, url.toString())
    }
  }
}
</script>

<style lang="scss">
// Import Main styles for this application,
@import 'assets/scss/style';

.app-update-notice {
  position: fixed;
  z-index: 2000;
  top: 12px;
  top: max(12px, env(safe-area-inset-top));
  left: 50%;
  display: flex;
  width: calc(100% - 24px);
  max-width: 720px;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid #87b8a2;
  border-radius: 10px;
  color: #173b2d;
  background: #f1fbf6;
  box-shadow: 0 14px 36px rgba(22, 64, 47, .2);
  transform: translateX(-50%);
}

.app-update-notice__icon {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  border-radius: 50%;
  color: #fff;
  background: #2d8a62;
}

.app-update-notice__content {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  line-height: 1.35;
}

.app-update-notice__content span {
  margin-top: 2px;
  color: #46685b;
  font-size: .875rem;
}

.app-update-notice__button {
  flex: 0 0 auto;
  padding: 9px 14px;
  border: 0;
  border-radius: 7px;
  color: #fff;
  background: #267653;
  font-weight: 700;
  cursor: pointer;
}

.app-update-notice__button:hover,
.app-update-notice__button:focus {
  background: #1c5c40;
  outline: 2px solid rgba(38, 118, 83, .28);
  outline-offset: 2px;
}

.app-update-enter-active,
.app-update-leave-active {
  transition: opacity .2s ease, transform .25s ease;
}

.app-update-enter,
.app-update-leave-to {
  opacity: 0;
  transform: translate(-50%, -14px);
}

.c-dark-theme .app-update-notice {
  border-color: #41795f;
  color: #e2f5eb;
  background: #20372d;
}

.c-dark-theme .app-update-notice__content span {
  color: #b9d3c7;
}

@media (max-width: 575.98px) {
  .app-update-notice {
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    flex-wrap: wrap;
  }

  .app-update-notice__icon {
    width: 32px;
    height: 32px;
    flex-basis: 32px;
  }

  .app-update-notice__content {
    width: calc(100% - 42px);
    flex-basis: calc(100% - 42px);
  }

  .app-update-notice__button {
    width: 100%;
  }
}
</style>
